import { redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { clientQueryOptions } from '@/lib/query/options';
import { createLogger } from '@/utils/logger';

const log = createLogger('activeClientResolver');

/**
 * Guarantees a client-role user has an active client selected before any
 * client-scoped route renders and fires API calls.
 *
 * WHY THIS EXISTS: every client-scoped backend call requires the
 * `X-Active-Client-Id` header (injected from `sessionStorage.selectedClient` by
 * clientContextInterceptor). If no client is selected, those calls return
 * `400 ACTIVE_CLIENT_REQUIRED`, which the UI renders as an empty binder list —
 * i.e. "we do not have a current binder". Previously the active client was only
 * resolved on the `/` route, so a multi-client user arriving via a deep link
 * (e.g. a notification email → `/audit-binders/{id}` or `/upload`) never got a
 * client selected and hit that failure. Running this from the root route closes
 * that gap for every protected client route.
 *
 * Resolution order:
 * - A client is already selected and still accessible → backfill the cached
 *   object if needed, then continue.
 * - Exactly one accessible client → auto-select it.
 * - More than one and none selected → redirect to `/client-select`, preserving
 *   the originally requested URL so the user returns there after choosing.
 * - No accessible clients → continue (the destination page shows its own empty
 *   state; nothing to select).
 *
 * Only call this for authenticated client-role users on protected client routes.
 * Must NOT be called for the `/client-select` route itself (it would loop).
 */
export async function ensureActiveClientSelected(params: {
  queryClient: QueryClient;
  token: string;
  /** Full href (pathname + search) of the route being entered, used as the client-select return target. */
  href: string;
}): Promise<void> {
  const { queryClient, token, href } = params;

  const availableClients = await queryClient.ensureQueryData(clientQueryOptions.availableClients(token));

  if (!Array.isArray(availableClients) || availableClients.length === 0) {
    // Nothing to select — let the destination page render its own empty state.
    return;
  }

  // `selectedClientId` is the source of truth, but recover it from the cached
  // `selectedClient` object if the two ever fall out of sync (e.g. the id key
  // was cleared but the object survived). This mirrors the interceptor's
  // fallback for the opposite direction and prevents a multi-client user with a
  // valid cached client from being bounced to /client-select needlessly.
  let storedClientId = sessionStorage.getItem('selectedClientId');
  if (!storedClientId) {
    const cached = sessionStorage.getItem('selectedClient');
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as { id?: string };
        if (parsed?.id) {
          storedClientId = parsed.id;
          sessionStorage.setItem('selectedClientId', parsed.id);
        }
      } catch {
        // Corrupt cached object — drop it and fall through to fresh resolution.
        sessionStorage.removeItem('selectedClient');
      }
    }
  }

  if (storedClientId) {
    const current = availableClients.find((c) => c.id === storedClientId);
    if (current) {
      // Keep sessionStorage.selectedClient in sync with the freshest client data
      // so the interceptor always has an accurate object to build the header from.
      sessionStorage.setItem('selectedClient', JSON.stringify(current));
      return;
    }
    // Stored selection is no longer accessible (revoked/stale) — drop it and re-resolve.
    log.debug('Stored client no longer accessible; re-resolving', { storedClientId });
    sessionStorage.removeItem('selectedClientId');
    sessionStorage.removeItem('selectedClient');
  }

  if (availableClients.length === 1) {
    const only = availableClients[0];
    if (only?.id) {
      sessionStorage.setItem('selectedClientId', only.id);
      sessionStorage.setItem('selectedClient', JSON.stringify(only));
    }
    return;
  }

  // Multiple clients and none selected — make the user choose, then return here.
  throw redirect({ to: '/client-select', search: { redirect: href } });
}
